import type { TOC } from '@ember/component/template-only';
import Heading from './heading';
import Text from './text';
import type { ComponentLike } from '@glint/template';
import type { ReimaginedColor } from 'tio-reimagined/color-tokens';
import Card from './card';

// ---------------------------------------------------------------------------
// FeatureCard – icon + title + description card for feature/info grids
// ---------------------------------------------------------------------------

/**
 * Color theme options for the feature card icon
 */
export type FeatureCardColor = ReimaginedColor;

interface FeatureCardSignature {
  Args: {
    /** Phosphor icon component */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Title text */
    title: string;
    /** Description text */
    description?: string;
    /** Color theme for icon badge */
    color?: FeatureCardColor;
    /** Center layout (larger icon, centered text) — for contact/method cards */
    centered?: boolean;
  };
  Blocks: {
    /** Optional action content at the bottom (button, link, chip) */
    action?: [];
  };
  Element: HTMLDivElement;
}

/**
 * FeatureCard - An icon + title + description card for feature/info grids.
 *
 * Composes against the existing `<Card>` component for the surface and the
 * `.app-icon-badge` family for the colored icon wrapper — no local color
 * lookup or icon-wrapper div needed.
 *
 * Two layouts:
 * - **Default:** Horizontal icon badge + title row, description below
 * - **Centered (@centered):** Larger centered icon, centered title/description, action block
 *
 * @example
 * ```gts
 * <FeatureCard
 *   @icon={{PhRocket}}
 *   @title="Increase by 1%"
 *   @description="Increasing your contribution by just 1% could add $150,000+."
 *   @color="emerald"
 * />
 * ```
 *
 * @example Centered layout
 * ```gts
 * <FeatureCard
 *   @icon={{PhEnvelope}}
 *   @title="Email Us"
 *   @description="…"
 *   @color="blue"
 *   @centered={{true}}
 * >
 *   <:action>
 *     <a href="mailto:support@tio.com">support@tio.com</a>
 *   </:action>
 * </FeatureCard>
 * ```
 */
const FeatureCard: TOC<FeatureCardSignature> = <template>
  {{#let (if @color @color "gray") as |color|}}
    <Card
      data-test-feature-card
      @padding="lg"
      class="{{if @centered 'text-center hover:shadow-lg transition-shadow'}}"
      ...attributes
    >
      {{#if @centered}}
        {{! Centered layout: large icon-badge centered on top }}
        <div class="app-icon-badge app-icon-badge--{{color}} app-icon-badge--lg mx-auto mb-4">
          {{#let @icon as |Icon|}}
            <Icon @size={{28}} />
          {{/let}}
        </div>
        <Heading
          data-test-feature-card-title
          @level={{4}}
          class="font-semibold text-[var(--text-primary)] mb-2"
        >
          {{@title}}
        </Heading>
      {{else}}
        {{! Default layout: icon-badge + title inline }}
        <div class="tio-row mb-4">
          <div class="app-icon-badge app-icon-badge--{{color}} app-icon-badge--md shrink-0">
            {{#let @icon as |Icon|}}
              <Icon @size={{20}} />
            {{/let}}
          </div>
          <Heading
            data-test-feature-card-title
            @level={{4}}
            class="font-semibold text-[var(--text-primary)]"
          >
            {{@title}}
          </Heading>
        </div>
      {{/if}}

      {{#if @description}}
        <Text
          data-test-feature-card-description
          class="text-[var(--text-tertiary)] text-sm leading-relaxed {{if @centered 'mb-4'}}"
        >
          {{@description}}
        </Text>
      {{/if}}

      {{#if (has-block "action")}}
        <div data-test-feature-card-action class="{{unless @centered 'mt-4'}}">
          {{yield to="action"}}
        </div>
      {{/if}}
    </Card>
  {{/let}}
</template>;

export default FeatureCard;

export type { FeatureCardSignature };
